import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useChangePassword } from '../../api/hooks/useUser';
import { Button } from '../actions/Button';
import { TextInput } from '../inputs/TextInput';
import { Alert } from '../feedback/Alert';

export function SecurityTab() {
  const changePassword = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (newPassword !== confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setErrors({ newPassword: 'Password must be at least 8 characters' });
      return;
    }

    try {
      await changePassword.mutateAsync({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });

      // Clear form on success
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccessMessage('Password changed successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        setErrors({ currentPassword: error.response.data.message });
      }
    }
  };

  return (
    <div className="max-w-lg">
      <h2 className="text-lg font-semibold text-text-primary mb-1">
        Change Password
      </h2>
      <p className="text-sm text-text-secondary mb-6">
        Update your password to keep your account secure.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <TextInput
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter current password"
            error={errors.currentPassword || errors.current_password}
          />
          <button
            type="button"
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            className="absolute right-3 top-[42px] text-text-secondary hover:text-text-primary"
          >
            {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        <div className="relative">
          <TextInput
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            hint="Must be at least 8 characters"
            error={errors.newPassword || errors.new_password}
          />
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute right-3 top-[42px] text-text-secondary hover:text-text-primary"
          >
            {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        <TextInput
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password"
          error={errors.confirmPassword}
        />

        {successMessage && (
          <Alert variant="success">{successMessage}</Alert>
        )}

        {changePassword.error && !Object.keys(errors).length && (
          <Alert variant="error">Failed to change password. Please try again.</Alert>
        )}

        <Button
          type="submit"
          disabled={!canSubmit || changePassword.isPending}
          fullWidth
        >
          {changePassword.isPending ? 'Changing Password...' : 'Change Password'}
        </Button>
      </form>
    </div>
  );
}
